import React from 'react';
import { HomeIcon, TrophyIcon, PlayCircleIcon, UsersIcon, RocketIcon, BookOpenIcon, ChevronRightIcon, CalendarIcon } from 'lucide-react';
import type { Section } from '../../App';
interface SidebarProps {
  activeSection: Section;
  setActiveSection: (section: Section) => void;
  collapsed: boolean;
  setCollapsed: (v: boolean) => void;
}
const navItems: {
  id: Section;
  label: string;
  icon: any;
  badge?: number;
}[] = [{
  id: 'dashboard',
  label: 'Dashboard',
  icon: HomeIcon
}, {
  id: 'halloffame',
  label: 'Hall of Fame',
  icon: TrophyIcon
}, {
  id: 'videos',
  label: 'Video Library',
  icon: PlayCircleIcon
}, {
  id: 'directory',
  label: 'Directory',
  icon: UsersIcon
}, {
  id: 'launchpad',
  label: 'Launchpad',
  icon: RocketIcon
}, {
  id: 'learning',
  label: 'Learning Center',
  icon: BookOpenIcon
}, {
  id: 'meetingroom',
  label: 'Meeting Rooms',
  icon: CalendarIcon
}];
export function Sidebar({
  activeSection,
  setActiveSection,
  collapsed,
  setCollapsed
}: SidebarProps) {
  return <aside style={{
    width: collapsed ? 72 : 260,
    minWidth: collapsed ? 72 : 260,
    background: '#1A2B3C',
    height: '100vh',
    display: 'flex',
    flexDirection: 'column',
    position: 'relative',
    flexShrink: 0,
    transition: 'width 0.3s cubic-bezier(0.4,0,0.2,1), min-width 0.3s cubic-bezier(0.4,0,0.2,1)',
    clipPath: collapsed ? 'none' : 'polygon(0 0, 100% 0, 100% calc(100% - 52px), calc(100% - 52px) 100%, 0 100%)',
    zIndex: 20,
    boxShadow: '4px 0 24px rgba(13,27,42,0.4)'
  }} data-id="element-13">
      {/* Diagonal stripe overlay */}
      <div className="diagonal-stripe" style={{
      position: 'absolute',
      inset: 0,
      pointerEvents: 'none',
      zIndex: 0
    }} data-id="element-14" />

      {/* Logo */}
      <div style={{
      padding: collapsed ? '20px 16px' : '20px 20px',
      borderBottom: '1px solid rgba(255,255,255,0.07)',
      display: 'flex',
      alignItems: 'center',
      gap: 12,
      position: 'relative',
      zIndex: 1
    }} data-id="element-15">
        <div style={{
        width: 38,
        height: 38,
        background: 'linear-gradient(135deg, #2B8DC4 0%, #1E6FA0 100%)',
        borderRadius: 10,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        transform: 'skewX(-6deg)',
        boxShadow: '0 4px 12px rgba(43,141,196,0.4)'
      }} data-id="element-16">
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" data-id="element-17">
            <path d="M2 12 C6 6, 14 4, 22 8 C18 8, 16 10, 18 14 C14 12, 10 14, 8 18 C8 14, 6 12, 2 12Z" fill="white" data-id="element-18" />
          </svg>
        </div>
        {!collapsed && <div style={{
        overflow: 'hidden'
      }} data-id="element-19">
            <div style={{
          fontWeight: 900,
          fontSize: 15,
          color: '#FFFFFF',
          fontStyle: 'italic',
          letterSpacing: '-0.02em',
          lineHeight: 1.1
        }} data-id="element-20">
              SWIFT
            </div>
            <div style={{
          fontSize: 10,
          color: '#2B8DC4',
          fontWeight: 700,
          letterSpacing: '0.12em',
          textTransform: 'uppercase'
        }} data-id="element-21">
              Home Loans
            </div>
          </div>}
      </div>

      {/* Nav Items */}
      <nav style={{
      flex: 1,
      padding: '12px 0',
      overflowY: 'auto',
      position: 'relative',
      zIndex: 1
    }} data-id="element-22">
        {navItems.map((item, idx) => {
        const Icon = item.icon;
        const isActive = activeSection === item.id;
        const accentColor = idx % 2 === 0 ? '#2B8DC4' : '#3FA8E0';
        return <button key={item.id} onClick={() => setActiveSection(item.id)} title={collapsed ? item.label : undefined} className="nav-arrow-active" style={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          padding: collapsed ? '11px 0' : '11px 20px',
          justifyContent: collapsed ? 'center' : 'flex-start',
          background: isActive ? `linear-gradient(90deg, ${accentColor}28 0%, transparent 100%)` : 'transparent',
          borderLeft: isActive ? `3px solid ${accentColor}` : '3px solid transparent',
          color: isActive ? '#FFFFFF' : 'rgba(255,255,255,0.5)',
          cursor: 'pointer',
          border: 'none',
          position: 'relative',
          transition: 'all 0.15s ease',
          textAlign: 'left'
        }} onMouseEnter={e => {
          if (!isActive) {
            e.currentTarget.style.color = 'rgba(255,255,255,0.85)';
            e.currentTarget.style.background = 'rgba(255,255,255,0.05)';
          }
        }} onMouseLeave={e => {
          if (!isActive) {
            e.currentTarget.style.color = 'rgba(255,255,255,0.5)';
            e.currentTarget.style.background = 'transparent';
          }
        }} data-id="element-23">
              {isActive && !collapsed && <div style={{
            position: 'absolute',
            right: 12,
            top: '50%',
            transform: 'translateY(-50%)',
            width: 0,
            height: 0,
            borderTop: '6px solid transparent',
            borderBottom: '6px solid transparent',
            borderLeft: `6px solid ${accentColor}`,
            opacity: 0.8
          }} data-id="element-24" />}
              <div style={{
            position: 'relative',
            flexShrink: 0
          }} data-id="element-25">
                <Icon size={18} strokeWidth={isActive ? 2.5 : 1.8} data-id="element-26" />
                {item.badge && <span style={{
              position: 'absolute',
              top: -6,
              right: -6,
              background: '#2B8DC4',
              color: 'white',
              fontSize: 9,
              fontWeight: 800,
              borderRadius: 10,
              minWidth: 16,
              height: 16,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '0 3px'
            }} data-id="element-27">
                    {item.badge}
                  </span>}
              </div>
              {!collapsed && <span style={{
            fontSize: 13,
            fontWeight: isActive ? 700 : 500,
            letterSpacing: isActive ? '-0.01em' : 0
          }} data-id="element-28">
                  {item.label}
                </span>}
              {collapsed && <div className="sidebar-tooltip" style={{
            position: 'absolute',
            left: '100%',
            marginLeft: 8,
            padding: '6px 10px',
            background: '#1A2B3C',
            color: 'white',
            fontSize: 12,
            fontWeight: 600,
            borderRadius: 6,
            whiteSpace: 'nowrap',
            opacity: 0,
            pointerEvents: 'none',
            zIndex: 50,
            boxShadow: '0 4px 12px rgba(0,0,0,0.3)'
          }} data-id="element-29">
                  {item.label}
                </div>}
            </button>;
      })}
      </nav>

      {/* Bottom: User + Collapse */}
      <div style={{
      borderTop: '1px solid rgba(255,255,255,0.07)',
      padding: collapsed ? '14px 0' : '14px 16px',
      display: 'flex',
      alignItems: 'center',
      gap: 10,
      justifyContent: collapsed ? 'center' : 'flex-start',
      position: 'relative',
      zIndex: 1
    }} data-id="element-30">
        <div style={{
        width: 34,
        height: 34,
        borderRadius: 10,
        background: 'linear-gradient(135deg, #2B8DC4, #1E6FA0)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: 12,
        fontWeight: 800,
        color: 'white',
        flexShrink: 0,
        transform: 'skewX(-4deg)'
      }} data-id="element-31">
          JD
        </div>
        {!collapsed && <>
            <div style={{
          flex: 1,
          minWidth: 0
        }} data-id="element-32">
              <div style={{
            fontSize: 12,
            fontWeight: 700,
            color: '#FFFFFF'
          }} data-id="element-33">
                Jamie Davis
              </div>
              <div style={{
            fontSize: 10,
            color: 'rgba(255,255,255,0.4)',
            fontWeight: 500
          }} data-id="element-34">
                Loan Originator
              </div>
            </div>
            <button onClick={() => setCollapsed(true)} style={{
          background: 'none',
          border: 'none',
          color: 'rgba(255,255,255,0.3)',
          cursor: 'pointer',
          padding: 4,
          borderRadius: 6,
          display: 'flex',
          alignItems: 'center'
        }} title="Collapse sidebar" data-id="element-35">
              <ChevronRightIcon size={14} style={{
            transform: 'rotate(180deg)'
          }} data-id="element-36" />
            </button>
          </>}
        {collapsed && <button onClick={() => setCollapsed(false)} style={{
        position: 'absolute',
        right: -12,
        top: '50%',
        transform: 'translateY(-50%)',
        width: 24,
        height: 24,
        borderRadius: '50%',
        background: '#2B8DC4',
        border: '2px solid #F4F8FC',
        color: 'white',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        boxShadow: '0 2px 8px rgba(43,141,196,0.4)',
        zIndex: 30
      }} title="Expand sidebar" data-id="element-37">
            <ChevronRightIcon size={12} data-id="element-38" />
          </button>}
      </div>
    </aside>;
}